import {
    Assessment as AssessmentIcon,
    Campaign as CampaignIcon,
    Close as CloseIcon,
    DataObject as DatabaseIcon,
    History as HistoryIcon,
    Home as HomeIcon,
    MonitorHeart as MonitorHeartIcon,
    NavigateNext as NavigateNextIcon,
    Refresh as RefreshIcon,
    Security as SecurityIcon,
    Storage as StorageIcon,
    Sync as SyncIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import expirationService from '../../services/ExpirationService';
import statusServiceInstance from '../../services/StatusService';
import axios from 'axios';
import Swal from 'sweetalert2';
import LockIcon from '@mui/icons-material/Lock';
import { isAdmin, getAuthHeaders } from '../../utils/authUtils';
import { PageHeader } from '../common/PageHeader';
import { diskNotice, diskSpaceLine } from './diskSpace';

const SystemStatusPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [loading, setLoading] = useState(true);
    const [serviceStatus, setServiceStatus] = useState({});
    const [backendVersion, setBackendVersion] = useState('');
    const [frontendVersion, setFrontEndVersion] = useState('');

    const [expirationData, setExpirationData] = useState({
        lastUpdated: '',
        expirations: [],
    });
    const [mongoSyncData, setMongoSyncData] = useState({
        loading: true,
        status: null,
        databaseSize: null,
        syncHistory: [],
        error: null,
    });
    const [syncTriggerLoading, setSyncTriggerLoading] = useState(false);
    const [fullSyncTriggerLoading, setFullSyncTriggerLoading] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info',
    });
    const [serverStatus, setServerStatus] = useState({
        loading: true,
        data: null,
        error: null,
    });
    const [resettingCount, setResettingCount] = useState(false);

    const navigate = useNavigate();

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleGoHome = () => {
        navigate('/');
    };

    useEffect(() => {
        const fetchStatus = async () => {
            setLoading(true);
            try {
                setServiceStatus({ allOperational: true });
            } catch (error) {
                console.error('Error fetching status information:', error);
                setServiceStatus({ allOperational: true });
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
    }, []);

    useEffect(() => {
        statusServiceInstance
            .getServerVersion()
            .then((response) => {
                const parsed = parseGitHistory(response);
                setBackendVersion(parsed);
            })
            .catch((error) => {
                setBackendVersion([
                    {
                        date: new Date().toISOString(),
                        commitId: 'ERROR',
                        message: 'Not able to read the version history of Amrut Vaani Backend',
                        isError: true,
                    },
                ]);
            });

        fetch('/frontend-githistory.txt')
            .then((response) => response.text())
            .then((data) => {
                const parsed = parseGitHistory(data);
                setFrontEndVersion(parsed);
            })
            .catch((error) => {
                setFrontEndVersion([
                    {
                        date: new Date().toISOString(),
                        commitId: 'ERROR',
                        message: 'Not able to read the version history of Amrut Vaani Frontend',
                        isError: true,
                    },
                ]);
            });
    }, []);

    useEffect(() => {
        const fetchExpirationData = async () => {
            const data = await expirationService.getExpirationDates();
            setExpirationData(data);
        };

        fetchExpirationData();
    }, []);

    useEffect(() => {
        const fetchMongoSyncData = async () => {
            setMongoSyncData((prev) => ({ ...prev, loading: true }));
            try {
                const [statusRes, sizeRes, historyRes] = await Promise.all([
                    statusServiceInstance.getMongoSyncStatus(),
                    statusServiceInstance.getMongoDatabaseSize(),
                    statusServiceInstance.getMongoSyncHistory(),
                ]);

                setMongoSyncData({
                    loading: false,
                    status: statusRes,
                    databaseSize: sizeRes,
                    syncHistory: historyRes,
                    error: null,
                });
            } catch (error) {
                console.error('Error fetching MongoDB sync data:', error);
                setMongoSyncData({
                    loading: false,
                    status: null,
                    databaseSize: null,
                    syncHistory: [],
                    error: 'Failed to load MongoDB sync information',
                });
            }
        };

        fetchMongoSyncData();
    }, []);

    // Poll every 5 seconds while a sync is in progress.
    // Kept in a separate effect so it reacts to live state changes and avoids stale closure.
    useEffect(() => {
        if (!mongoSyncData.status?.syncInProgress) return;

        const interval = setInterval(async () => {
            try {
                const statusRes = await statusServiceInstance.getMongoSyncStatus();
                setMongoSyncData((prev) => ({ ...prev, status: statusRes }));
            } catch (error) {
                console.error('Error polling sync status:', error);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [mongoSyncData.status?.syncInProgress]);

    useEffect(() => {
        const fetchServerStatus = async () => {
            try {
                const status = await statusServiceInstance.getServerStatus();
                setServerStatus({
                    loading: false,
                    data: status,
                    error: null,
                });
            } catch (error) {
                console.error('Error fetching server status:', error);
                setServerStatus({
                    loading: false,
                    data: null,
                    error: 'Failed to load server status',
                });
            }
        };

        fetchServerStatus();
        // Refresh every minute
        const interval = setInterval(fetchServerStatus, 60000);
        return () => clearInterval(interval);
    }, []);

    const showNotification = (message, severity = 'info') => {
        setNotification({
            open: true,
            message,
            severity,
        });

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
            setNotification((prev) => ({
                ...prev,
                open: false,
            }));
        }, 5000);
    };

    const handleTriggerSync = async () => {
        if (!isAdmin()) {
            showNotification('Only admin users can trigger database synchronization', 'warning');
            return;
        }

        try {
            setSyncTriggerLoading(true);
            const response = await statusServiceInstance.triggerMongoSync();

            if (response.status === 'IN_PROGRESS') {
                showNotification('MongoDB synchronization has been triggered successfully', 'success');

                // Refresh status after triggering so the polling effect activates
                setTimeout(async () => {
                    try {
                        const statusRes = await statusServiceInstance.getMongoSyncStatus();
                        setMongoSyncData((prev) => ({
                            ...prev,
                            status: statusRes,
                        }));
                    } catch (error) {
                        console.error('Error refreshing sync status:', error);
                    }
                    setSyncTriggerLoading(false);
                }, 2000);
            } else {
                showNotification(response.message || 'Failed to trigger sync', 'error');
                setSyncTriggerLoading(false);
            }
        } catch (error) {
            console.error('Error triggering MongoDB sync:', error);
            showNotification('Error triggering MongoDB sync', 'error');
            setSyncTriggerLoading(false);
        }
    };

    const handleTriggerFullSync = async () => {
        if (!isAdmin()) {
            showNotification('Only admin users can trigger full database synchronization', 'warning');
            return;
        }
        try {
            setFullSyncTriggerLoading(true);
            const response = await statusServiceInstance.triggerMongoFullSync();
            if (response.status === 'IN_PROGRESS') {
                showNotification('Full MongoDB synchronization has been triggered. All collections will be synced.', 'success');
                setTimeout(async () => {
                    try {
                        const statusRes = await statusServiceInstance.getMongoSyncStatus();
                        setMongoSyncData((prev) => ({ ...prev, status: statusRes }));
                    } catch (error) {
                        console.error('Error refreshing sync status:', error);
                    }
                    setFullSyncTriggerLoading(false);
                }, 2000);
            } else {
                showNotification(response.message || 'Failed to trigger full sync', 'error');
                setFullSyncTriggerLoading(false);
            }
        } catch (error) {
            console.error('Error triggering full MongoDB sync:', error);
            showNotification('Error triggering full MongoDB sync', 'error');
            setFullSyncTriggerLoading(false);
        }
    };

    const parseGitHistory = (historyText) => {
        if (!historyText) {
            return [];
        }

        try {
            if (historyText.includes('Not able to read the version history')) {
                return [
                    {
                        date: new Date().toISOString(),
                        commitId: 'N/A',
                        message: historyText.replace(/<\/?li>/g, ''),
                        isError: true,
                    },
                ];
            }

            const items = historyText.match(/<li>(.*?)<\/li>/g) || [];

            return items.map((item) => {
                const cleanItem = item.replace(/<\/?li>/g, '').trim();
                const parts = cleanItem.split(' - ');

                if (parts.length >= 3) {
                    return {
                        date: parts[0],
                        commitId: parts[1].trim(),
                        message: parts.slice(2).join(' - ').trim(),
                        isError: false,
                    };
                } else {
                    return {
                        date: 'Unknown',
                        commitId: 'Unknown',
                        message: cleanItem,
                        isError: false,
                    };
                }
            });
        } catch (error) {
            console.error('Error parsing git history:', error);
            return [
                {
                    date: new Date().toISOString(),
                    commitId: 'N/A',
                    message: 'Error parsing version history',
                    isError: true,
                },
            ];
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const renderHealthStatus = () => (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Server Health
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={3} md={4}>
                        <Typography variant="subtitle2">Active Threads</Typography>
                        <Typography>{serverStatus.data.activeThreadCount}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Typography variant="subtitle2">Uptime</Typography>
                        <Typography>{new Date(serverStatus.data.serverStartTime).toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={3} md={4}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2">Duplicate Video Count</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>{serverStatus.data.duplicateVideoCount}</Typography>
                                {isAdmin() && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleResetDuplicateCount}
                                        disabled={resettingCount || serverStatus.data.duplicateVideoCount === 0}
                                        startIcon={resettingCount ? <CircularProgress size={16} /> : null}
                                        sx={{ ml: 1, fontSize: '0.7rem' }}
                                    >
                                        Reset
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">Disk Space (Base Folder)</Typography>
                        <Typography>{diskSpaceLine(serverStatus.data)}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const handleResetDuplicateCount = async () => {
        if (!isAdmin()) {
            showNotification('Only admin users can reset duplicate video count', 'warning');
            return;
        }

        try {
            setResettingCount(true);
            await statusServiceInstance.resetDuplicateVideoCount();

            // Refresh server status to reflect the changes
            try {
                const status = await statusServiceInstance.getServerStatus();
                setServerStatus({
                    loading: false,
                    data: status,
                    error: null,
                });
                showNotification('Duplicate video count has been reset', 'success');
            } catch (error) {
                console.error('Error refreshing server status:', error);
                showNotification('Count reset, but failed to refresh status', 'warning');
            }
        } catch (error) {
            console.error('Error resetting duplicate video count:', error);
            showNotification('Failed to reset duplicate video count', 'error');
        } finally {
            setResettingCount(false);
        }
    };

    const renderTranslationStatus = () => (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Translation Status
                </Typography>
                {serverStatus.data.translationState ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Current Progress
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={
                                    (serverStatus.data.translationState.current /
                                        serverStatus.data.translationState.total) *
                                    100
                                }
                                sx={{ height: 10, borderRadius: 5 }}
                            />
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">State</Typography>
                                <Chip
                                    label={serverStatus.data.translationState.state}
                                    color={
                                        serverStatus.data.translationState.state === 'PENDING' ? 'warning' : 'primary'
                                    }
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">Status</Typography>
                                <Typography variant="body2">{serverStatus.data.translationState.status}</Typography>
                            </Grid>
                            {serverStatus.data.translationState.languageFile && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">Processing File</Typography>
                                    <Typography variant="body2">
                                        {serverStatus.data.translationState.languageFile}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </>
                ) : (
                    <Typography color="text.secondary">No active translations</Typography>
                )}
            </CardContent>
        </Card>
    );

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}
        >
            <Paper
                sx={{
                    borderRadius: 0,
                    padding: 0,
                }}
            >
                <PageHeader
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                    themeVariant={themeVariant}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, padding: 1 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/')}
                        sx={{ mr: 2 }}
                    >
                        Home
                    </Button>
                    {/* Add arrow icon between buttons */}
                    <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Button variant="text" color="secondary" startIcon={<MonitorHeartIcon />} sx={{ mr: 2 }}>
                        System
                    </Button>
                    <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Button variant="text" color="secondary" startIcon={<AssessmentIcon />} sx={{ mr: 2 }}>
                        System Status
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CampaignIcon />}
                        onClick={() => navigate('/release-notes')}
                        sx={{ ml: 'auto' }}
                    >
                        Release Notes
                    </Button>
                    {isAdmin() && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<LockIcon />}
                            onClick={async () => {
                                // Opens maintenance scheduling dialog for admins
                                try {
                                    const apiBase = process.env.REACT_APP_API_URL;
                                    const statusResp = await axios.get(`${apiBase}/api/maintenance`);
                                    const status = statusResp.data || {};

                                    if (status.active) {
                                        const res = await Swal.fire({
                                            title: 'Maintenance scheduled',
                                            html: `Active until <b>${status.endsAt ? new Date(status.endsAt).toLocaleString() : 'unknown'}</b><br/>Message: ${status.message || ''}`,
                                            showCancelButton: true,
                                            confirmButtonText: 'Cancel maintenance',
                                            cancelButtonText: 'Close',
                                        });

                                        if (res.isConfirmed) {
                                            const headers = getAuthHeaders(true);
                                            const cancelResp = await axios.post(`${apiBase}/api/v3/admin/maintenance/cancel`, {}, { headers });
                                            if (cancelResp.data && cancelResp.data.success) {
                                                Swal.fire('Cancelled', 'Maintenance cancelled', 'success');
                                                const fresh = await statusServiceInstance.getServerStatus();
                                                setServerStatus({ loading: false, data: fresh, error: null });
                                            } else {
                                                Swal.fire('Error', cancelResp.data?.message || 'Failed to cancel', 'error');
                                            }
                                        }

                                        return;
                                    }

                                    const { value: formValues } = await Swal.fire({
                                        title: 'Schedule maintenance',
                                        html:
                                            '<input id="ms-duration" type="number" min="1" value="20" class="swal2-input" placeholder="Duration minutes">' +
                                            '<textarea id="ms-message" class="swal2-textarea" placeholder="Message (optional)"></textarea>',
                                        focusConfirm: false,
                                        showCancelButton: true,
                                        preConfirm: () => {
                                            const duration = parseInt(document.getElementById('ms-duration').value || '20', 10);
                                            const message = document.getElementById('ms-message').value || '';
                                            return { duration, message };
                                        },
                                    });

                                    if (formValues) {
                                        const headers = getAuthHeaders(true);
                                        const scheduleResp = await axios.post(
                                            `${apiBase}/api/v3/admin/maintenance`,
                                            { durationMinutes: formValues.duration, message: formValues.message },
                                            { headers },
                                        );

                                        if (scheduleResp.data && scheduleResp.data.success) {
                                            Swal.fire('Scheduled', 'Maintenance scheduled', 'success');
                                            const fresh = await statusServiceInstance.getServerStatus();
                                            setServerStatus({ loading: false, data: fresh, error: null });
                                        } else {
                                            Swal.fire('Error', scheduleResp.data?.message || 'Failed to schedule', 'error');
                                        }
                                    }
                                } catch (err) {
                                    console.error('Maintenance admin error', err);
                                    Swal.fire('Error', err.message || 'An error occurred', 'error');
                                }
                            }}
                            sx={{ ml: 2 }}
                        >
                            Maintenance
                        </Button>
                    )}
                </Box>
                <Box sx={{ mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
                    {notification.open && (
                        <Alert
                            severity={notification.severity}
                            sx={{
                                mb: 2,
                                position: 'relative',
                                '& .MuiAlert-action': {
                                    alignItems: 'center',
                                },
                            }}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setNotification((prev) => ({ ...prev, open: false }))}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                        >
                            {notification.message}
                        </Alert>
                    )}

                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                        Version Information
                    </Typography>
                    <Paper sx={{ p: 3, mb: 4 }} className="version-information">
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ mb: { xs: 3, md: 0 } }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle1">Backend Version</Typography>
                                        <Chip
                                            label="Server"
                                            sx={{
                                                color: 'secondary.main',
                                            }}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(0, 0, 0, 0.03)',
                                            p: 2,
                                            borderRadius: 1,
                                            maxHeight: '300px',
                                            overflow: 'auto',
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {backendVersion ? (
                                            Array.isArray(backendVersion) ? (
                                                <List dense disablePadding>
                                                    {backendVersion.map((commit, i) => (
                                                        <ListItem key={i} sx={{ display: 'block', py: 0.5 }}>
                                                            {commit.isError ? (
                                                                <Alert severity="warning" sx={{ mb: 0 }}>
                                                                    {commit.message}
                                                                </Alert>
                                                            ) : (
                                                                <>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                        }}
                                                                    >
                                                                        <span>{commit.message}</span>
                                                                        <Chip
                                                                            size="small"
                                                                            variant="outlined"
                                                                            label={commit.commitId}
                                                                            sx={{
                                                                                ml: 1,
                                                                                fontSize: '0.7rem',
                                                                                height: 20,
                                                                            }}
                                                                        />
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        Released: {commit.date}
                                                                    </Typography>
                                                                    {i < backendVersion.length - 1 && (
                                                                        <Divider sx={{ my: 1 }} />
                                                                    )}
                                                                </>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : typeof backendVersion === 'string' ? (
                                                <Alert severity="warning" sx={{ mb: 0 }}>
                                                    {backendVersion.replace(/<\/?li>/g, '')}
                                                </Alert>
                                            ) : (
                                                <Typography variant="body2">
                                                    No version information available
                                                </Typography>
                                            )
                                        ) : (
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle1">Frontend Version</Typography>
                                        <Chip
                                            label="Client"
                                            sx={{
                                                color: 'secondary.main',
                                            }}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(0, 0, 0, 0.03)',
                                            p: 2,
                                            borderRadius: 1,
                                            maxHeight: '300px',
                                            overflow: 'auto',
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {frontendVersion ? (
                                            Array.isArray(frontendVersion) ? (
                                                <List dense disablePadding>
                                                    {frontendVersion.map((commit, i) => (
                                                        <ListItem key={i} sx={{ display: 'block', py: 0.5 }}>
                                                            {commit.isError ? (
                                                                <Alert severity="warning" sx={{ mb: 0 }}>
                                                                    {commit.message}
                                                                </Alert>
                                                            ) : (
                                                                <>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                        }}
                                                                    >
                                                                        <span>{commit.message}</span>
                                                                        <Chip
                                                                            size="small"
                                                                            variant="outlined"
                                                                            label={commit.commitId}
                                                                            sx={{
                                                                                ml: 1,
                                                                                fontSize: '0.7rem',
                                                                                height: 20,
                                                                            }}
                                                                        />
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        Released: {commit.date}
                                                                    </Typography>
                                                                    {i < frontendVersion.length - 1 && (
                                                                        <Divider sx={{ my: 1 }} />
                                                                    )}
                                                                </>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : typeof frontendVersion === 'string' ? (
                                                <Alert severity="warning" sx={{ mb: 0 }}>
                                                    {frontendVersion.replace(/<\/?li>/g, '')}
                                                </Alert>
                                            ) : (
                                                <Typography variant="body2">
                                                    No version information available
                                                </Typography>
                                            )
                                        ) : (
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Typography variant="caption" color="text.secondary">
                                Last checked: {new Date().toLocaleString()}
                            </Typography>
                        </Box>
                    </Paper>

                    <Paper sx={{ p: 3, mb: 4 }} className="credentials-status">
                        <Typography variant="h6" gutterBottom>
                            Credentials Status
                        </Typography>
                        {expirationData.expirations.length > 0 &&
                            expirationService.checkForExpiringItems(expirationData.expirations).length > 0 && (
                                <Alert severity="warning" sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2">
                                        <SecurityIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        One or more credentials are expiring within the next 7 days!
                                    </Typography>
                                </Alert>
                            )}

                        <Grid container spacing={2}>
                            {expirationData.expirations.map((item) => {
                                const daysRemaining = expirationService.calculateDaysRemaining(item.expirationDate);
                                const isExpiring = daysRemaining <= 7;

                                return (
                                    <Grid item xs={12} sm={6} key={item.name}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                p: 2.5,
                                                height: '100%',
                                                borderRadius: 1,
                                                bgcolor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.02)',
                                                border: '1px solid',
                                                borderColor: isExpiring ? 'warning.main' : 'divider',
                                                '&:hover': {
                                                    bgcolor: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? 'rgba(255, 255, 255, 0.08)'
                                                            : 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <Box sx={{ mr: 2, mt: 0.5 }}>
                                                <SecurityIcon
                                                    color={isExpiring ? 'warning' : 'primary'}
                                                    fontSize="large"
                                                />
                                            </Box>
                                            <Box sx={{ width: '100%' }}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary">
                                                    Expires: {new Date(item.expirationDate).toLocaleString()}
                                                </Typography>
                                                {daysRemaining !== null && (
                                                    <Chip
                                                        label={`${daysRemaining} days remaining`}
                                                        size="medium"
                                                        color={
                                                            daysRemaining <= 3
                                                                ? 'error'
                                                                : daysRemaining <= 7
                                                                ? 'warning'
                                                                : 'success'
                                                        }
                                                        sx={{ mt: 2, fontSize: '0.875rem' }}
                                                    />
                                                )}
                                                {item.description && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        display="block"
                                                        sx={{ mt: 2 }}
                                                    >
                                                        {item.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {expirationData.lastUpdated && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Last updated: {new Date(expirationData.lastUpdated).toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    <Paper sx={{ p: 3, mb: 4 }} className="server-status">
                        <Typography variant="h6" gutterBottom>
                            Server Status
                        </Typography>
                        {serverStatus.loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress />
                            </Box>
                        ) : serverStatus.error ? (
                            <Alert severity="error">{serverStatus.error}</Alert>
                        ) : (
                            <>
                                {diskNotice(serverStatus.data) && (
                                    <Alert severity={diskNotice(serverStatus.data).severity} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2">
                                            <StorageIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                            {diskNotice(serverStatus.data).text}
                                        </Typography>
                                    </Alert>
                                )}
                                {renderHealthStatus()}
                                {renderTranslationStatus()}
                            </>
                        )}
                    </Paper>

                    <Paper sx={{ p: 3, mb: 4 }} className="mongodb-sync-status">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                <DatabaseIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                MongoDB Sync Status
                            </Typography>
                            {isAdmin() && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        startIcon={
                                            syncTriggerLoading ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : (
                                                <SyncIcon />
                                            )
                                        }
                                        onClick={handleTriggerSync}
                                        disabled={syncTriggerLoading || fullSyncTriggerLoading || mongoSyncData.status?.syncInProgress || false}
                                    >
                                        {mongoSyncData.status?.syncInProgress ? 'Sync In Progress' : 'Trigger Sync'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        startIcon={
                                            fullSyncTriggerLoading ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : (
                                                <SyncIcon />
                                            )
                                        }
                                        onClick={handleTriggerFullSync}
                                        disabled={syncTriggerLoading || fullSyncTriggerLoading || mongoSyncData.status?.syncInProgress || false}
                                    >
                                        Trigger Full Sync
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {mongoSyncData.loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : mongoSyncData.error ? (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {mongoSyncData.error}
                            </Alert>
                        ) : (
                            <>
                                {/* Sync Status Summary */}
                                <Box sx={{ mb: 3 }}>
                                    <Alert
                                        severity={
                                            mongoSyncData.status?.syncInProgress
                                                ? 'info'
                                                : mongoSyncData.status?.lastSync?.syncComplete
                                                ? 'success'
                                                : 'warning'
                                        }
                                        icon={mongoSyncData.status?.syncInProgress ? <RefreshIcon /> : undefined}
                                        sx={{ mb: 2 }}
                                    >
                                        {mongoSyncData.status?.syncInProgress ? (
                                            <Typography>MongoDB synchronization is currently in progress</Typography>
                                        ) : mongoSyncData.status?.lastSync ? (
                                            <Typography>
                                                Last sync:{' '}
                                                {mongoSyncData.status.lastSync.syncComplete
                                                    ? 'Completed successfully'
                                                    : 'Completed with issues'}{' '}
                                                ({formatDate(mongoSyncData.status.lastSync.endTime)})
                                            </Typography>
                                        ) : (
                                            <Typography>No sync has been performed yet</Typography>
                                        )}
                                    </Alert>
                                </Box>

                                <Grid container spacing={3}>
                                    {/* Last Sync Details */}
                                    <Grid item xs={12} md={6}>
                                        <Box
                                            sx={{
                                                bgcolor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.02)',
                                                p: 2,
                                                borderRadius: 1,
                                                height: '100%',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                                                <SyncIcon
                                                    sx={{ fontSize: '1.2rem', verticalAlign: 'text-bottom', mr: 1 }}
                                                />
                                                Last Sync Details
                                            </Typography>

                                            {mongoSyncData.status?.lastSync ? (
                                                <List dense>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Sync Mode"
                                                            secondary={
                                                                mongoSyncData.status.lastSync.syncMode || 'Unknown'
                                                            }
                                                        />
                                                    </ListItem>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Start Time"
                                                            secondary={formatDate(
                                                                mongoSyncData.status.lastSync.startTime,
                                                            )}
                                                        />
                                                    </ListItem>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="End Time"
                                                            secondary={formatDate(
                                                                mongoSyncData.status.lastSync.endTime,
                                                            )}
                                                        />
                                                    </ListItem>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Duration"
                                                            secondary={`${Math.round(
                                                                (new Date(mongoSyncData.status.lastSync.endTime) -
                                                                    new Date(mongoSyncData.status.lastSync.startTime)) /
                                                                    1000,
                                                            )} seconds`}
                                                        />
                                                    </ListItem>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Collections Synced"
                                                            secondary={`${mongoSyncData.status.lastSync.syncedCollections} / ${mongoSyncData.status.lastSync.totalCollections}`}
                                                        />
                                                    </ListItem>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Documents Synced"
                                                            secondary={`${mongoSyncData.status.lastSync.syncedDocuments} / ${mongoSyncData.status.lastSync.totalDocuments}`}
                                                        />
                                                    </ListItem>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Success Rate"
                                                            secondary={`${mongoSyncData.status.lastSync.syncSuccessRate.toFixed(
                                                                1,
                                                            )}%`}
                                                        />
                                                    </ListItem>
                                                    {mongoSyncData.status.lastSync.failedCollections &&
                                                        mongoSyncData.status.lastSync.failedCollections.length > 0 && (
                                                            <ListItem>
                                                                <ListItemText
                                                                    primary="Failed Collections"
                                                                    secondary={mongoSyncData.status.lastSync.failedCollections.join(
                                                                        ', ',
                                                                    )}
                                                                />
                                                            </ListItem>
                                                        )}
                                                </List>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                                    No previous sync information available
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    {/* Database Size Information */}
                                    <Grid item xs={12} md={6}>
                                        <Box
                                            sx={{
                                                bgcolor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.02)',
                                                p: 2,
                                                borderRadius: 1,
                                                height: '100%',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                                                <StorageIcon
                                                    sx={{ fontSize: '1.2rem', verticalAlign: 'text-bottom', mr: 1 }}
                                                />
                                                Database Size Information
                                            </Typography>

                                            {mongoSyncData.databaseSize ? (
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Local Database
                                                            </Typography>
                                                            <List dense disablePadding>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Data Size"
                                                                        secondary={formatBytes(
                                                                            mongoSyncData.databaseSize.localDbSizeBytes,
                                                                        )}
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Storage Size"
                                                                        secondary={formatBytes(
                                                                            mongoSyncData.databaseSize
                                                                                .localStorageSizeBytes,
                                                                        )}
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Collections"
                                                                        secondary={
                                                                            mongoSyncData.databaseSize
                                                                                .localNumCollections
                                                                        }
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Documents"
                                                                        secondary={mongoSyncData.databaseSize.localNumDocuments?.toLocaleString()}
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Collection Names"
                                                                        secondary={
                                                                            mongoSyncData.databaseSize.localCollections?.join(
                                                                                ', ',
                                                                            ) || 'N/A'
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            </List>
                                                        </Paper>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Atlas Database
                                                            </Typography>
                                                            <List dense disablePadding>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Data Size"
                                                                        secondary={formatBytes(
                                                                            mongoSyncData.databaseSize.atlasDbSizeBytes,
                                                                        )}
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Storage Size"
                                                                        secondary={formatBytes(
                                                                            mongoSyncData.databaseSize
                                                                                .atlasStorageSizeBytes,
                                                                        )}
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Collections"
                                                                        secondary={
                                                                            mongoSyncData.databaseSize
                                                                                .atlasNumCollections
                                                                        }
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Documents"
                                                                        secondary={mongoSyncData.databaseSize.atlasNumDocuments?.toLocaleString()}
                                                                    />
                                                                </ListItem>
                                                                <ListItem disablePadding>
                                                                    <ListItemText
                                                                        primary="Collection Names"
                                                                        secondary={
                                                                            mongoSyncData.databaseSize.atlasCollections?.join(
                                                                                ', ',
                                                                            ) || 'N/A'
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            </List>
                                                        </Paper>
                                                    </Grid>
                                                </Grid>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                                    Database size information not available
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    {/* Sync History */}
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                bgcolor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.02)',
                                                p: 2,
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                                                <HistoryIcon
                                                    sx={{ fontSize: '1.2rem', verticalAlign: 'text-bottom', mr: 1 }}
                                                />
                                                Recent Sync History
                                            </Typography>

                                            {mongoSyncData.syncHistory && mongoSyncData.syncHistory.length > 0 ? (
                                                <Box sx={{ overflowX: 'auto' }}>
                                                    <table
                                                        style={{
                                                            width: '100%',
                                                            borderCollapse: 'collapse',
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        <thead>
                                                            <tr
                                                                style={{
                                                                    borderBottom: '1px solid',
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <th style={{ padding: '8px', textAlign: 'left' }}>
                                                                    Start Time
                                                                </th>
                                                                <th style={{ padding: '8px', textAlign: 'left' }}>
                                                                    Mode
                                                                </th>
                                                                <th style={{ padding: '8px', textAlign: 'right' }}>
                                                                    Documents
                                                                </th>
                                                                <th style={{ padding: '8px', textAlign: 'right' }}>
                                                                    Collections
                                                                </th>
                                                                <th style={{ padding: '8px', textAlign: 'right' }}>
                                                                    Duration
                                                                </th>
                                                                <th style={{ padding: '8px', textAlign: 'right' }}>
                                                                    Status
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {mongoSyncData.syncHistory
                                                                .sort(
                                                                    (a, b) =>
                                                                        new Date(b.startTime) - new Date(a.startTime),
                                                                )
                                                                .map((sync, index) => (
                                                                    <tr
                                                                        key={sync.syncId || index}
                                                                        style={{
                                                                            borderBottom: '1px solid',
                                                                            borderColor: 'rgba(0,0,0,0.12)',
                                                                            backgroundColor: sync.syncComplete
                                                                                ? undefined
                                                                                : 'rgba(255, 235, 59, 0.1)',
                                                                        }}
                                                                    >
                                                                        <td style={{ padding: '8px' }}>
                                                                            {formatDate(sync.startTime)}
                                                                        </td>
                                                                        <td style={{ padding: '8px' }}>
                                                                            <Chip
                                                                                label={sync.syncMode || 'Unknown'}
                                                                                size="small"
                                                                                color={
                                                                                    sync.syncMode === 'incremental'
                                                                                        ? 'info'
                                                                                        : 'secondary'
                                                                                }
                                                                                variant="outlined"
                                                                            />
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                padding: '8px',
                                                                                textAlign: 'right',
                                                                            }}
                                                                        >
                                                                            {sync.syncedDocuments?.toLocaleString()} /{' '}
                                                                            {sync.totalDocuments?.toLocaleString()}
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                padding: '8px',
                                                                                textAlign: 'right',
                                                                            }}
                                                                        >
                                                                            {sync.syncedCollections} /{' '}
                                                                            {sync.totalCollections}
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                padding: '8px',
                                                                                textAlign: 'right',
                                                                            }}
                                                                        >
                                                                            {sync.endTime
                                                                                ? `${Math.round(
                                                                                      (new Date(sync.endTime) -
                                                                                          new Date(sync.startTime)) /
                                                                                          1000,
                                                                                  )}s`
                                                                                : 'In progress'}
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                padding: '8px',
                                                                                textAlign: 'right',
                                                                            }}
                                                                        >
                                                                            <Chip
                                                                                label={
                                                                                    sync.syncComplete
                                                                                        ? 'Complete'
                                                                                        : 'Partial'
                                                                                }
                                                                                size="small"
                                                                                color={
                                                                                    sync.syncComplete
                                                                                        ? 'success'
                                                                                        : 'warning'
                                                                                }
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </table>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                                    No sync history available
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Paper>
                </Box>
            </Paper>
        </Box>
    );
};

export default SystemStatusPage;
